import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTheme, useLanguage } from '../context/AppContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import SenArtisteLogo from '../components/SenArtisteLogo';
import LanguageSelector from '../components/LanguageSelector';
import { 
  Sun, Moon, ArrowLeft, Code, Briefcase, TrendingUp, Rocket,
  Phone, Mail, Linkedin, Github, Globe, CheckCircle, 
  GraduationCap, Users, Lightbulb, Award
} from 'lucide-react';

const About = () => {
  const { theme, toggleTheme } = useTheme();
  const { t, language } = useLanguage();

  const parcours = language === 'fr' ? [
    {
      icon: <GraduationCap className="w-6 h-6" />,
      title: "Ingénieur",
      period: "Formation",
      description: "Formation d'ingénieur avec spécialisation en informatique et systèmes d'information.",
      color: "from-blue-500 to-blue-600"
    },
    {
      icon: <Code className="w-6 h-6" />,
      title: "Développeur",
      period: "Début de carrière",
      description: "Développement d'applications web et mobiles, maîtrise des technologies modernes (React, Node.js, Python, bases de données).",
      color: "from-green-500 to-green-600"
    },
    {
      icon: <Briefcase className="w-6 h-6" />,
      title: "Business Analyst",
      period: "Évolution",
      description: "Analyse des besoins métier, rédaction de spécifications fonctionnelles, interface entre les équipes techniques et business.",
      color: "from-purple-500 to-purple-600"
    },
    {
      icon: <TrendingUp className="w-6 h-6" />,
      title: "Chef de Projet / Responsable Application",
      period: "Management",
      description: "Pilotage de projets IT, gestion d'équipes, coordination des parties prenantes, livraison de solutions à forte valeur ajoutée.",
      color: "from-orange-500 to-orange-600"
    },
    {
      icon: <Rocket className="w-6 h-6" />,
      title: "Entrepreneur",
      period: "Aujourd'hui",
      description: "Fondateur de DKR Consulting, accompagnement des entreprises dans leur transformation digitale et développement de solutions sur mesure.",
      color: "from-primary to-yellow-500"
    }
  ] : [
    {
      icon: <GraduationCap className="w-6 h-6" />,
      title: "Engineer",
      period: "Education",
      description: "Engineering degree with specialization in computer science and information systems.",
      color: "from-blue-500 to-blue-600"
    },
    {
      icon: <Code className="w-6 h-6" />,
      title: "Developer",
      period: "Early career",
      description: "Web and mobile application development, mastery of modern technologies (React, Node.js, Python, databases).",
      color: "from-green-500 to-green-600"
    },
    {
      icon: <Briefcase className="w-6 h-6" />,
      title: "Business Analyst",
      period: "Evolution",
      description: "Business needs analysis, functional specifications writing, interface between technical and business teams.",
      color: "from-purple-500 to-purple-600"
    },
    {
      icon: <TrendingUp className="w-6 h-6" />,
      title: "Project Manager / Application Manager",
      period: "Management",
      description: "IT project management, team leadership, stakeholder coordination, delivery of high-value solutions.",
      color: "from-orange-500 to-orange-600"
    },
    {
      icon: <Rocket className="w-6 h-6" />,
      title: "Entrepreneur",
      period: "Today",
      description: "Founder of DKR Consulting, supporting companies in their digital transformation and developing custom solutions.",
      color: "from-primary to-yellow-500"
    }
  ];

  const services = language === 'fr' ? [
    {
      icon: <Code className="w-8 h-8" />,
      title: "Développement Web & Mobile",
      description: "Applications web modernes, sites vitrines, e-commerce, applications mobiles iOS/Android."
    },
    {
      icon: <Lightbulb className="w-8 h-8" />,
      title: "Conseil & Stratégie Digitale",
      description: "Audit de l'existant, définition de la roadmap digitale, choix technologiques, accompagnement au changement."
    },
    {
      icon: <Users className="w-8 h-8" />,
      title: "Gestion de Projet IT",
      description: "Pilotage de projets en méthode Agile ou traditionnelle, coordination des équipes, reporting et suivi budgétaire."
    },
    {
      icon: <Award className="w-8 h-8" />,
      title: "Formation & Coaching",
      description: "Montée en compétences des équipes, ateliers pratiques, accompagnement des profils juniors."
    }
  ] : [
    {
      icon: <Code className="w-8 h-8" />,
      title: "Web & Mobile Development",
      description: "Modern web applications, showcase websites, e-commerce, iOS/Android mobile applications."
    },
    {
      icon: <Lightbulb className="w-8 h-8" />,
      title: "Digital Consulting & Strategy",
      description: "Existing audit, digital roadmap definition, technology choices, change management support."
    },
    {
      icon: <Users className="w-8 h-8" />,
      title: "IT Project Management",
      description: "Project management using Agile or traditional methods, team coordination, reporting and budget tracking."
    },
    {
      icon: <Award className="w-8 h-8" />,
      title: "Training & Coaching",
      description: "Team skill development, practical workshops, junior profile support."
    }
  ];

  const whyChooseMe = language === 'fr' ? [
    { title: "Vision 360°", desc: "Une compréhension globale des enjeux techniques ET business grâce à mon parcours polyvalent." },
    { title: "Pragmatisme", desc: "Des solutions concrètes et adaptées à votre budget, pas de sur-engineering inutile." },
    { title: "Engagement", desc: "Un interlocuteur unique, impliqué de A à Z dans la réussite de votre projet." }
  ] : [
    { title: "360° Vision", desc: "A comprehensive understanding of both technical AND business challenges thanks to my versatile background." },
    { title: "Pragmatism", desc: "Concrete solutions adapted to your budget, no unnecessary over-engineering." },
    { title: "Commitment", desc: "A single point of contact, involved from A to Z in the success of your project." }
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-border">
        <div className="max-w-6xl mx-auto px-2 py-1 flex items-center justify-between">
          <Link to="/">
            <Button variant="ghost">
              <ArrowLeft className="w-4 h-4 mr-2" />
              {t('back')}
            </Button>
          </Link>
          
          <div className="flex items-center gap-3">
            <SenArtisteLogo className="w-14 h-14" />
            <h1 className="font-syne font-bold text-xl">{t('about')}</h1>
          </div>
          
          <div className="flex items-center gap-2">
            <LanguageSelector />
            <Button variant="ghost" size="icon" onClick={toggleTheme}>
              {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-12">
        {/* Hero Section */}
        <motion.section 
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="w-40 h-40 mx-auto mb-6 rounded-full overflow-hidden shadow-2xl bg-white dark:bg-gray-900 flex items-center justify-center p-2">
            <img 
              src="/logo-dkr-consulting.png" 
              alt="DKR Consulting" 
              className="w-full h-full object-contain"
            />
          </div>
          
          <h1 className="font-syne text-4xl md:text-5xl font-bold mb-4">
            DKR Consulting
          </h1>
          
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto mb-6">
            {language === 'fr' 
              ? "De l'ingénierie au conseil, en passant par le développement et la gestion de projet : une expertise complète au service de votre transformation digitale."
              : "From engineering to consulting, through development and project management: complete expertise at the service of your digital transformation."}
          </p>
          
          <div className="flex flex-wrap justify-center gap-4">
            <a href="https://www.dkr-consulting.com" target="_blank" rel="noopener noreferrer">
              <Button variant="outline" size="lg" className="btn-primary">
                <Globe className="w-4 h-4 mr-2" />
                {language === 'fr' ? 'Visiter mon site en ligne' : 'Visit my website'}
              </Button>
            </a>
            <a href="tel:+33619446736">
              <Button variant="outline" size="lg">
                <Phone className="w-4 h-4 mr-2" />
                +33 6 19 44 67 36
              </Button>
            </a>
            <a href="tel:+221778312002">
              <Button variant="outline" size="lg">
                <Phone className="w-4 h-4 mr-2" />
                +221 77 831 20 02
              </Button>
            </a>
            <a href="mailto:contact@dkr-consulting.com">
              <Button size="lg" className="btn-primary">
                <Mail className="w-4 h-4 mr-2" />
                contact@dkr-consulting.com
              </Button>
            </a>
          </div>
        </motion.section>

        {/* Parcours */}
        <motion.section 
          className="mb-16"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
        >
          <h2 className="font-syne text-3xl font-bold text-center mb-12">
            {language === 'fr' ? 'Mon Parcours' : 'My Journey'}
          </h2>
          
          <div className="relative">
            {/* Timeline line */}
            <div className="absolute left-1/2 transform -translate-x-1/2 w-1 h-full bg-gradient-to-b from-primary via-purple-500 to-green-500 hidden md:block" />
            
            <div className="space-y-8">
              {parcours.map((item, index) => (
                <motion.div
                  key={index}
                  className={`flex items-center gap-8 ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}
                  initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.1 * index }}
                >
                  <div className={`flex-1 ${index % 2 === 0 ? 'md:text-right' : 'md:text-left'}`}>
                    <Card className="glass-card inline-block">
                      <CardContent className="p-6">
                        <div className={`inline-flex items-center justify-center w-12 h-12 rounded-full bg-gradient-to-r ${item.color} text-white mb-4`}>
                          {item.icon}
                        </div>
                        <h3 className="font-syne text-xl font-bold mb-1">{item.title}</h3>
                        <p className="text-sm text-primary mb-2">{item.period}</p>
                        <p className="text-muted-foreground">{item.description}</p>
                      </CardContent>
                    </Card>
                  </div>
                  
                  {/* Timeline dot */}
                  <div className="hidden md:flex items-center justify-center w-4 h-4 rounded-full bg-primary border-4 border-background z-10" />
                  
                  <div className="flex-1 hidden md:block" />
                </motion.div>
              ))}
            </div>
          </div>
        </motion.section>

        {/* Services */}
        <motion.section 
          className="mb-16"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
        >
          <h2 className="font-syne text-3xl font-bold text-center mb-4">
            {language === 'fr' ? 'Mes Services' : 'My Services'}
          </h2>
          <p className="text-center text-muted-foreground mb-12 max-w-2xl mx-auto">
            {language === 'fr' 
              ? 'Un accompagnement sur mesure pour concrétiser vos projets digitaux'
              : 'Tailored support to bring your digital projects to life'}
          </p>
          
          <div className="grid md:grid-cols-2 gap-6">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 * index }}
              >
                <Card className="glass-card h-full hover:border-primary transition-colors">
                  <CardContent className="p-6">
                    <div className="w-16 h-16 rounded-xl bg-primary/10 flex items-center justify-center text-primary mb-4">
                      {service.icon}
                    </div>
                    <h3 className="font-syne text-xl font-bold mb-2">{service.title}</h3>
                    <p className="text-muted-foreground">{service.description}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </motion.section>

        {/* Pourquoi me choisir */}
        <motion.section 
          className="mb-16"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
        >
          <Card className="glass-card overflow-hidden">
            <CardContent className="p-8 md:p-12">
              <h2 className="font-syne text-3xl font-bold text-center mb-8">
                {language === 'fr' ? 'Pourquoi Travailler Avec Moi ?' : 'Why Work With Me?'}
              </h2>
              
              <div className="grid md:grid-cols-3 gap-6">
                {whyChooseMe.map((item, index) => (
                  <div key={index} className="text-center">
                    <CheckCircle className="w-10 h-10 mx-auto text-primary mb-3" />
                    <h4 className="font-semibold text-lg mb-2">{item.title}</h4>
                    <p className="text-sm text-muted-foreground">{item.desc}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </motion.section>

        {/* Contact CTA */}
        <motion.section 
          className="text-center"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
        >
          <Card className="bg-gradient-to-r from-primary/20 to-yellow-500/20 border-primary">
            <CardContent className="p-8 md:p-12">
              <h2 className="font-syne text-3xl font-bold mb-4">
                {language === 'fr' ? 'Un Projet en Tête ?' : 'Got a Project in Mind?'}
              </h2>
              <p className="text-muted-foreground mb-8 max-w-xl mx-auto">
                {language === 'fr' 
                  ? "Discutons de vos besoins et voyons ensemble comment je peux vous accompagner dans la réalisation de vos ambitions digitales."
                  : "Let's discuss your needs and see together how I can help you achieve your digital ambitions."}
              </p>
              
              <div className="flex flex-wrap justify-center gap-4 mb-8">
                <a href="https://www.dkr-consulting.com" target="_blank" rel="noopener noreferrer">
                  <Button size="lg" className="btn-primary">
                    <Globe className="w-4 h-4 mr-2" />
                    {language === 'fr' ? 'Visiter mon site en ligne' : 'Visit my website'}
                  </Button>
                </a>
                <a href="tel:+33619446736">
                  <Button size="lg" variant="outline" className="w-full sm:w-auto">
                    <Phone className="w-4 h-4 mr-2" />
                    France: +33 6 19 44 67 36
                  </Button>
                </a>
                <a href="tel:+221778312002">
                  <Button size="lg" variant="outline" className="w-full sm:w-auto">
                    <Phone className="w-4 h-4 mr-2" />
                    +221 77 831 20 02
                  </Button>
                </a>
              </div>
              
              <a href="mailto:contact@dkr-consulting.com">
                <Button size="lg" className="btn-primary text-lg px-8">
                  <Mail className="w-5 h-5 mr-2" />
                  {t('contact')}
                </Button>
              </a>
              
              <div className="flex justify-center gap-4 mt-8">
                <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-primary transition-colors">
                  <Linkedin className="w-6 h-6" />
                </a>
                <a href="https://github.com" target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-primary transition-colors">
                  <Github className="w-6 h-6" />
                </a>
                <a href="https://dkr-consulting.com" target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-primary transition-colors">
                  <Globe className="w-6 h-6" />
                </a>
              </div>
            </CardContent>
          </Card>
        </motion.section>
      </main>

      {/* Footer */}
      <footer className="border-t border-border py-8 mt-12">
        <div className="max-w-6xl mx-auto px-4 text-center text-sm text-muted-foreground">
          <p>© {new Date().getFullYear()} DKR Consulting. {t('allRightsReserved')}.</p>
          <p className="mt-2">
            <strong>Sen Artiste</strong> - {language === 'fr' ? 'Plateforme développée avec' : 'Platform developed with'} ❤️ {language === 'fr' ? 'par' : 'by'} DKR Consulting
          </p>
        </div>
      </footer>
    </div>
  );
};

export default About;
